class ServiceLocator extends HTMLElement {
    constructor() {
        super();
        
        // Check if Mapbox key exists in theme settings
        
        const mapboxKey = window.platformScripts?.api?.mapbox;
        
        if (!mapboxKey) {
            console.error('Mapbox key not found in theme settings');
            this.showMessage('Map service unavailable. Please check configuration.', 'error');
            return;
        }
        
        // Set the access token
        if (typeof mapboxgl !== 'undefined') {
            mapboxgl.accessToken = mapboxKey;
        } else {
            console.error('Mapbox GL JS not loaded');
            this.showMessage('Map service unavailable. Please refresh the page.', 'error');
            return;
        }
        
        this.dataMapZoom = parseFloat(this.getAttribute('data-map-zoom')) || 6;
        this.dataMapLongitude = parseFloat(this.getAttribute('data-map-long')) || -2.3;
        this.dataMapLatitude = parseFloat(this.getAttribute('data-map-lat')) || 54.5;
        this.dataMapDistanceUnit = this.getAttribute('data-distance') || 'miles';
        this.defaultRadius = parseInt(this.getAttribute('data-default-radius')) || 25;
        
        this.serviceLocations = window.postcodeServiceData?.locations || window.theme?.service_locator?.locations || [];
        
        this.selectedMarker = null;
        this.currentPopup = null;
        this.activeContainer = null;
        this.mapMarkers = [];
        this.lastSearchCoordinates = null;
        this.lastSearchTerm = null;
        
        // Bind event handlers
        this.handlePostcodeSearch = this.handlePostcodeSearch.bind(this);
        this.handleCurrentLocation = this.handleCurrentLocation.bind(this);
        this.handleResultClick = this.handleResultClick.bind(this);
    }

    connectedCallback() {
        // Check if required scripts are loaded
        if (typeof mapboxgl === 'undefined') {
            console.error('Mapbox GL JS not loaded');
            this.showMessage('Map service unavailable. Please refresh the page.', 'error');
            return;
        }
        
        this.initializeElements();
        this.initializeMap();
        this.bindEvents();
    }

    initializeElements() {
        this.postcodeInput = this.querySelector('#postcode-input');
        this.searchButton = this.querySelector('.postcode-search-btn');
        this.currentLocationBtn = this.querySelector('button[name="current-location"]');
        this.messageContainer = this.querySelector('.service-locator__message');
        this.activeContainer = this.getActiveContainer();
    }

    initializeMap() {
        try {
            this.map = new mapboxgl.Map({
                container: 'map',
                style: 'mapbox://styles/mapbox/light-v11',
                center: [this.dataMapLongitude, this.dataMapLatitude],
                zoom: this.dataMapZoom,
            });

            this.map.on('load', () => {
                this.loadServiceMarkers();
            });

            this.map.on('error', (e) => {
                console.error('Map error:', e);
                this.showMessage('Map failed to load. Please try again.', 'error');
            });
            
        } catch (error) {
            console.error('Error initializing map:', error);
            this.showMessage('Map initialization failed. Please check your internet connection.', 'error');
        }
    }

    bindEvents() {
        if (this.searchButton) {
            this.searchButton.addEventListener('click', this.handlePostcodeSearch);
        }
        
        if (this.postcodeInput) {
            this.postcodeInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.handlePostcodeSearch();
                }
            });
        }
        

        
        if (this.currentLocationBtn) {
            this.currentLocationBtn.addEventListener('click', this.handleCurrentLocation);
        }
        
        if (this.activeContainer) {
            this.activeContainer.addEventListener('click', this.handleResultClick);
        }

        // Handle responsive container switching
        let debounceTimer;
        window.addEventListener('resize', () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const newActiveContainer = this.getActiveContainer();
                if (newActiveContainer !== this.activeContainer) {
                    this.transferResults();
                    this.activeContainer = newActiveContainer;
                }
            }, 100);
        });
    }

    async handlePostcodeSearch() {
        const postcode = this.postcodeInput ? this.postcodeInput.value.trim() : '';
        if (!postcode) {
            this.showMessage('Please enter a postcode', 'error');
            return;
        }

        try {
            this.showMessage('Searching...', 'loading');
            const coordinates = await this.geocodePostcode(postcode);
            
            if (coordinates) {
                this.searchNearbyServices(coordinates, postcode);
            } else {
                this.showMessage('Invalid postcode. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Postcode search error:', error);
            this.showMessage('Error searching postcode. Please try again.', 'error');
        }
    }

    async geocodePostcode(postcode) {
        const mapboxKey = window.platformScripts?.api?.mapbox;
        if (!mapboxKey) {
            console.error('No Mapbox key available for geocoding');
            return null;
        }

        try {
            // Use Mapbox Geocoding API for UK postcodes
            const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(postcode)}.json?access_token=${mapboxKey}&country=GB&types=postcode`;
            
            const response = await fetch(url);
            const data = await response.json();
            
            if (data.features && data.features.length > 0) {
                return data.features[0].center; // [longitude, latitude]
            }
            return null;
        } catch (error) {
            console.error('Geocoding error:', error);
            return null;
        }
    }

    handleCurrentLocation() {
        if (!navigator.geolocation) {
            this.showMessage('Geolocation is not supported by this browser.', 'error');
            return;
        }

        this.showMessage('Getting your location...', 'loading');
        
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const coordinates = [position.coords.longitude, position.coords.latitude];
                this.searchNearbyServices(coordinates, 'your current location');
            },
            (error) => {
                console.error('Geolocation error:', error);
                if (error.code === error.PERMISSION_DENIED) {
                    this.showMessage('Location access denied. Please enter your postcode instead.', 'error');
                } else {
                    this.showMessage('Unable to get your location. Please enter your postcode instead.', 'error');
                }
            }
        );
    }

    searchNearbyServices(coordinates, searchTerm) {
        this.lastSearchCoordinates = coordinates;
        this.lastSearchTerm = searchTerm;
        
        // Pass the postcode as well for postal code region matching
        const nearbyServices = this.findNearbyServices(coordinates, searchTerm);
        
        this.displaySearchResults(nearbyServices, coordinates, searchTerm);
        this.updateMapWithResults(coordinates, nearbyServices);
        this.clearMessage();
    }

    findNearbyServices(coordinates, userPostcode = null) {
        return this.serviceLocations
            .map(location => {
                let locationData;
                
                try {
                    // Handle both old JSON string format and new object format
                    locationData = typeof location === 'string' ? JSON.parse(location) : location;
                } catch (error) {
                    console.error('Error parsing location data:', error, location);
                    return null;
                }

                // Check if service has postal code regions defined
                if (locationData.postal_codes && locationData.postal_codes.length > 0 && userPostcode) {
                    // Use postal code region logic
                    if (this.isPostcodeInServiceArea(userPostcode, locationData.postal_codes)) {
                        return {
                            ...locationData,
                            distance: 0, // No distance for postal code matches
                            matchType: 'postal_code'
                        };
                    } else {
                        // Postcode not in service area
                        return null;
                    }
                }

                // Fall back to radius-based logic (backward compatibility)
                if (!locationData.virtual_service && (!locationData.longitude || !locationData.latitude)) {
                    console.error('Missing longitude or latitude for service:', locationData);
                    return null;
                }

                let distance = 0;
                if (!locationData.virtual_service && locationData.longitude && locationData.latitude) {
                    distance = this.calculateDistance(coordinates, [
                        locationData.longitude,
                        locationData.latitude
                    ]);
                }

                const serviceRadius = locationData.service_radius || this.defaultRadius;
                
                if (locationData.virtual_service) {
                    return {
                        ...locationData,
                        distance: distance,
                        matchType: 'radius'
                    };
                }
                
                // Mobile and regular services must be within radius
                if (distance <= serviceRadius) {
                    return {
                        ...locationData,
                        distance: distance,
                        matchType: 'radius'
                    };
                }
                
                return null;
            })
            .filter(location => location !== null)
            .sort((a, b) => a.distance - b.distance);
    }

    displaySearchResults(services, coordinates, searchTerm) {
        const resultsContainer = this.getActiveContainer();
        if (!resultsContainer) {
            console.error('No results container found');
            return;
        }

        resultsContainer.innerHTML = '';

        if (services.length === 0) {
                    resultsContainer.innerHTML = `
            <div class="no-results" style="text-align: center; padding: 40px 20px;">
                <h3 style="font-size: 1.3rem; margin-bottom: 12px; color: #666;">No services found</h3>
                <p style="font-size: 1rem; color: #888; margin-bottom: 20px;">Sorry, we couldn't find any services near ${searchTerm}. Try expanding your search area or contact us for assistance.</p>
            </div>
        `;
            return;
        }

        const resultsHtml = services.map(service => this.createServiceResultHtml(service)).join('');
        resultsContainer.innerHTML = `
            <div class="results-header" style="margin-bottom: 20px;">
                <h3 style="font-size: 1.4rem; margin-bottom: 8px;">${services.length} service${services.length !== 1 ? 's' : ''} found near ${searchTerm}</h3>
                <p class="results-subtitle" style="font-size: 1rem; color: #666;">Perfect for gifting to someone special in this area!</p>
            </div>
            <div class="results-list">
                ${resultsHtml}
            </div>
        `;
    }

    createServiceResultHtml(service) {
        // Determine distance display based on match type
        let distanceText;
        if (service.matchType === 'postal_code') {
            distanceText = 'Serves your postal district';
        } else if (service.virtual_service) {
            distanceText = 'Virtual Service';
        } else {
            distanceText = `${service.distance.toFixed(1)} ${this.dataMapDistanceUnit} away`;
        }
        
        // Create product URL - if no product handle, link to a general product page or contact
        const productUrl = service.product_handle ? `/products/${service.product_handle}` : '/pages/contact';
        
        // Show postal codes if available
        let postalCodesHtml = '';
        if (service.postal_codes && service.postal_codes.length > 0) {
            const displayCodes = service.postal_codes.slice(0, 5); // Show max 5
            const remainingCount = service.postal_codes.length - 5;
            postalCodesHtml = `
                <div class="service-item__postal-codes">
                    <strong>Serving:</strong> ${displayCodes.join(', ')}${remainingCount > 0 ? ` +${remainingCount} more` : ''}
                </div>
            `;
        }
        
        return `
            <div class="service-item" 
                 data-lng="${service.longitude || ''}" 
                 data-lat="${service.latitude || ''}"
                 data-service-id="${service.product_id || service.product_handle}"
                 data-product-id="${service.product_id}"
                 data-product-handle="${service.product_handle}"
                 data-match-type="${service.matchType || 'radius'}">
                <div class="service-item__header service-item__clickable">
                    <div>
                        <h4 class="service-item__name">${service.business_name || 'Local Service Provider'}</h4>
                        <span class="service-item__distance">${distanceText}</span>
                    </div>
                    <button class="service-item__toggle" aria-label="Toggle details">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <div class="service-item__body" style="display: none;">
                    <div class="service-item__details">
                        <p class="service-item__description">${service.product_title || 'Professional service available in your area'}</p>
                        <div class="service-item__badges">
                            ${service.virtual_service ? '<span class="service-badge service-badge--virtual">Virtual Service</span>' : ''}
                            ${service.mobile_service ? '<span class="service-badge service-badge--mobile">Mobile Service</span>' : ''}
                            ${service.matchType === 'postal_code' ? '<span class="service-badge service-badge--postal">Postal District Match</span>' : ''}
                        </div>
                        ${postalCodesHtml}
                    </div>
                    <div class="service-item__actions">
                        ${service.matchType !== 'postal_code' && service.longitude && service.latitude ? `
                            <button class="button button--secondary button--small view-on-map-btn">
                                View on Map
                            </button>
                        ` : ''}
                        <a href="${productUrl}" class="button button--primary button--small">
                            Order as Gift
                        </a>
                    </div>
                </div>
            </div>
        `;
    }



    updateMapWithResults(centerCoordinates, services) {
        if (!this.map) return;

        // Clear existing markers
        this.mapMarkers.forEach(marker => marker.remove());
        this.mapMarkers = [];

        // Fly to search location
        this.map.flyTo({
            center: centerCoordinates,
            zoom: 10
        });

        // Add markers for each service that has coordinates
        services.forEach(async (service) => {
            // Skip services without coordinates (postal code matches without lat/lng)
            if (!service.longitude || !service.latitude) {
                return;
            }
            
            const popupHtml = await this.createMapPopupHtml(service);
            const marker = new mapboxgl.Marker({
                color: '#76996B' // MumBloom green
            })
            .setLngLat([service.longitude, service.latitude])
            .setPopup(
                new mapboxgl.Popup({ offset: 25 })
                .setHTML(popupHtml)
            )
            .addTo(this.map);

            // Store service ID on marker for precise matching
            marker._serviceId = service.product_id;
            marker._serviceHandle = service.product_handle;

            this.mapMarkers.push(marker);
        });
    }

    async createMapPopupHtml(service) {
        const productUrl = service.product_handle ? `/products/${service.product_handle}` : '/pages/contact';
        
        // Get product image if product handle exists
        let productImageHtml = '';
        if (service.product_handle) {
            try {
                const productResponse = await fetch(`/products/${service.product_handle}.js`);
                if (productResponse.ok) {
                    const productData = await productResponse.json();
                    if (productData.featured_image) {
                        const imageUrl = productData.featured_image.replace('.jpg', '_150x150.jpg').replace('.png', '_150x150.png');
                        productImageHtml = `<img src="${imageUrl}" alt="${productData.title}" class="popup-product-image" style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px; margin-bottom: 10px;">`;
                    }
                }
                            } catch (error) {
                    // Could not load product image
                }
        }
        
        return `
            <div class="map-popup" style="min-width: 220px; text-align: center;">
                ${productImageHtml}
                <h4 style="margin: 0 0 8px 0; font-size: 1.3rem;">${service.business_name || 'Service Available'}</h4>
                <p style="margin: 0 0 10px 0; color: #666; font-size: 1rem;">${service.product_title || 'Local Provider'}</p>
                ${service.virtual_service ? '<span class="service-badge" style="display: inline-block; background: #9b59b6; color: white; padding: 4px 10px; border-radius: 4px; font-size: 0.9rem; margin-bottom: 10px;">Virtual Service</span>' : ''}
                ${service.mobile_service ? '<span class="service-badge" style="display: inline-block; background: #9fa58a; color: white; padding: 4px 10px; border-radius: 4px; font-size: 0.9rem; margin-bottom: 10px;">Mobile Service</span>' : ''}
                <p style="margin: 0 0 15px 0; color: #666; font-size: 0.95rem;">Available in your area</p>
                <div class="popup-actions">
                    <a href="${productUrl}" class="button button--primary button--small">
                        Order as Gift
                    </a>
                </div>
            </div>
        `;
    }



    handleResultClick(event) {
        const serviceItem = event.target.closest('.service-item');
        const viewMapBtn = event.target.closest('.view-on-map-btn');
        const toggleBtn = event.target.closest('.service-item__toggle');
        const clickableHeader = event.target.closest('.service-item__clickable');
        
        // Handle toggle button or clickable header
        if (serviceItem && (toggleBtn || clickableHeader)) {
            event.preventDefault();
            const body = serviceItem.querySelector('.service-item__body');
            const toggle = serviceItem.querySelector('.service-item__toggle');
            
            if (body) {
                const isOpen = body.style.display !== 'none';
                body.style.display = isOpen ? 'none' : 'block';
                
                // Rotate toggle icon
                if (toggle) {
                    toggle.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)';
                    toggle.style.transition = 'transform 0.3s ease';
                }
            }
            return;
        }
        
        // Handle view on map button
        if (serviceItem && viewMapBtn) {
            event.preventDefault();
            const serviceId = serviceItem.dataset.productId;
            const serviceHandle = serviceItem.dataset.productHandle;
            const lat = parseFloat(serviceItem.dataset.lat);
            const lng = parseFloat(serviceItem.dataset.lng);
            
            if (this.map && lat && lng) {
                // Fly to location
                this.map.flyTo({
                    center: [lng, lat],
                    zoom: 14
                });
                
                // Close all existing popups first
                this.mapMarkers.forEach(marker => {
                    const popup = marker.getPopup();
                    if (popup && popup.isOpen()) {
                        popup.remove();
                    }
                });
                
                // Find and open only the matching marker
                let foundMarker = null;
                
                // First try to match by service ID (most precise)
                if (serviceId) {
                    foundMarker = this.mapMarkers.find(marker => 
                        marker._serviceId && marker._serviceId.toString() === serviceId.toString()
                    );
                }
                
                // Fallback to handle match
                if (!foundMarker && serviceHandle) {
                    foundMarker = this.mapMarkers.find(marker => 
                        marker._serviceHandle === serviceHandle
                    );
                }
                
                // Fallback to coordinate match (with tight tolerance)
                if (!foundMarker) {
                    foundMarker = this.mapMarkers.find(marker => {
                        const markerLng = marker.getLngLat().lng;
                        const markerLat = marker.getLngLat().lat;
                        return Math.abs(markerLng - lng) < 0.00001 && Math.abs(markerLat - lat) < 0.00001;
                    });
                }
                
                // Open the popup for the found marker
                if (foundMarker) {
                    foundMarker.togglePopup();
                }
            }
        }
    }

    loadServiceMarkers() {
        if (!this.map) return;

        // Initial load of all service markers on map
        this.serviceLocations.forEach(async (location) => {
            let locationData;
            
            try {
                // Handle both old JSON string format and new object format
                locationData = typeof location === 'string' ? JSON.parse(location) : location;
            } catch (error) {
                console.error('Error parsing location data:', error, location);
                return;
            }

            // Validate required fields
            if (!locationData.longitude || !locationData.latitude) {
                console.error('Missing longitude or latitude for service:', locationData);
                return;
            }
            
            const popupHtml = await this.createMapPopupHtml(locationData);
            const marker = new mapboxgl.Marker({
                color: '#76996B',
                scale: 0.8
            })
            .setLngLat([locationData.longitude, locationData.latitude])
            .setPopup(
                new mapboxgl.Popup({ offset: 25 })
                .setHTML(popupHtml)
            )
            .addTo(this.map);
            
            // Store service ID on marker for precise matching
            marker._serviceId = locationData.product_id;
            marker._serviceHandle = locationData.product_handle;
            
            this.mapMarkers.push(marker);
        });
    }

    /**
     * Extract postal district from a full UK postcode
     * Examples: "M1 4AE" → "M1", "OL10 2BB" → "OL10", "SW1A 1AA" → "SW1A", "HP4 1AA" → "HP4"
     */
    extractPostalDistrict(postcode) {
        if (!postcode) return null;
        
        // Convert to uppercase and trim
        const cleaned = postcode.toUpperCase().trim();
        
        // UK postcodes format: "DISTRICT SECTOR" (e.g., "HP4 1AA")
        // The district is the outward code (everything before the space)
        
        // If there's a space, the district is everything before it
        const spaceIndex = cleaned.indexOf(' ');
        if (spaceIndex > 0) {
            return cleaned.substring(0, spaceIndex);
        }
        
        // If no space (incomplete postcode), try to extract district pattern
        // This handles cases where user hasn't finished typing
        const match = cleaned.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)/);
        return match ? match[1] : null;
    }

    /**
     * Check if a user's postcode is covered by a service's postal code regions
     * @param {string} userPostcode - Full user postcode (e.g., "M1 4AE")
     * @param {array|string|null} servicePostalCodes - Array or comma-separated string of postal districts
     * @returns {boolean}
     */
    isPostcodeInServiceArea(userPostcode, servicePostalCodes) {
        if (!userPostcode || !servicePostalCodes) return false;
        
        // Extract district from user's postcode
        const userDistrict = this.extractPostalDistrict(userPostcode);
        if (!userDistrict) return false;
        
        // Handle array or string input
        let postcodeList = [];
        if (Array.isArray(servicePostalCodes)) {
            postcodeList = servicePostalCodes;
        } else if (typeof servicePostalCodes === 'string') {
            postcodeList = servicePostalCodes.split(',').map(code => code.trim().toUpperCase());
        } else {
            return false;
        }
        
        // Check if user's district is in the service's list
        return postcodeList.some(code => {
            const cleanCode = code.trim().toUpperCase();
            return cleanCode === userDistrict;
        });
    }

    calculateDistance(coord1, coord2) {
        const R = 6371; // Earth's radius in kilometers
        const dLat = this.toRadians(coord2[1] - coord1[1]);
        const dLon = this.toRadians(coord2[0] - coord1[0]);
        const a = 
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRadians(coord1[1])) * 
            Math.cos(this.toRadians(coord2[1])) * 
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        let distance = R * c;

        if (this.dataMapDistanceUnit === 'miles') {
            distance *= 0.621371;
        }

        return distance;
    }

    toRadians(degrees) {
        return degrees * Math.PI / 180;
    }

    getActiveContainer() {
        return window.innerWidth > 1024
            ? document.getElementById('search-results')
            : document.getElementById('search-results-mobile');
    }

    transferResults() {
        const sourceContainer = this.activeContainer;
        const targetContainer = sourceContainer === document.getElementById('search-results')
            ? document.getElementById('search-results-mobile')
            : document.getElementById('search-results');

        if (sourceContainer && targetContainer) {
            while (sourceContainer.firstChild) {
                targetContainer.appendChild(sourceContainer.firstChild);
            }
        }
    }

    showMessage(message, type = 'info') {
        if (this.messageContainer) {
            this.messageContainer.innerHTML = `
                <div class="message message--${type}">
                    ${message}
                </div>
            `;
        }
    }

    clearMessage() {
        if (this.messageContainer) {
            this.messageContainer.innerHTML = '';
        }
    }
}

// Define the custom element
customElements.define('service-locator', ServiceLocator);